import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { ROUTES, SUB_TAB_GROUPS, SEARCH_INDEX } from 'config/navConfig';
import { ROLE_PROFILES, DEMO_USER } from 'config/roleConfig';
import { MOCK_ASSETS, MOCK_DEALS, MOCK_DOCUMENTS, MOCK_PEOPLE } from 'data/mockData';
import { useRole } from 'context/RoleContext';
import { NOTIFICATIONS_SEED } from 'pages/notifications/components/notificationsData';
import { THREADS } from 'pages/communications-hub/components/communicationsData';

// Quick links shown in spotlight when query is empty
const QUICK_LINKS = [
  { label: 'Dashboard',          path: ROUTES.dashboard,          icon: 'LayoutDashboard' },
  { label: 'Operations Console', path: ROUTES.operationsConsole,  icon: 'Monitor' },
  { label: 'Data Room',          path: ROUTES.dataRoom,           icon: 'FolderLock' },
  { label: 'ESG Reporting',      path: ROUTES.esgReporting,       icon: 'BarChart3' },
  { label: 'Pipeline',           path: ROUTES.pipeline,           icon: 'GitBranch' },
  { label: 'Token Governance',   path: ROUTES.tokenGovernance,    icon: 'Vote' },
];

// Spotlight search data
const ALL_PAGES    = SEARCH_INDEX.map((p) => ({ ...p, category: 'Pages' }));
const ASSETS_DATA  = MOCK_ASSETS.map((a)   => ({ ...a, category: 'Assets' }));
const DEALS_DATA   = MOCK_DEALS.map((d)    => ({ ...d, category: 'Deals' }));
const DOCS_DATA    = MOCK_DOCUMENTS.map((doc) => ({ ...doc, category: 'Documents' }));
const PEOPLE_DATA  = MOCK_PEOPLE.map((p)   => ({ ...p, category: 'People' }));

// BottomStrip section detection
const SECTION_TABS = [
  {
    key: 'assetManagement',
    label: 'ASSET MANAGEMENT',
    test: (p) => p.startsWith('/asset-management'),
    tabs: SUB_TAB_GROUPS.assetManagement,
  },
  {
    key: 'transactions',
    label: 'TRANSACTIONS',
    test: (p) => p.startsWith('/transactions'),
    tabs: SUB_TAB_GROUPS.transactions,
  },
  {
    key: 'deals',
    label: 'DEALS',
    test: (p) => p.startsWith('/deals'),
    tabs: SUB_TAB_GROUPS.deals,
  },
  {
    key: 'portfolio',
    label: 'PORTFOLIO',
    test: (p) => p.startsWith('/portfolio') || p === ROUTES.dashboard,
    tabs: SUB_TAB_GROUPS.portfolio,
  },
];

// Flat pages that show a breadcrumb instead of tabs
const FLAT_PAGE_LABELS = {
  [ROUTES.opportunities]:     { section: 'Marketplace',              icon: 'TrendingUp' },
  [ROUTES.esgReporting]:      { section: 'ESG Reporting',            icon: 'BarChart3' },
  [ROUTES.dataRoom]:          { section: 'Data Room',                icon: 'FolderLock' },
  [ROUTES.dealRoom]:          { section: 'Deal Room',                icon: 'FolderOpen' },
  [ROUTES.activityFeed]:      { section: 'Activity Feed',            icon: 'Activity' },
  [ROUTES.messages]:          { section: 'Messages',                 icon: 'MessageSquare' },
  [ROUTES.notifications]:     { section: 'Notifications',            icon: 'Bell' },
  [ROUTES.settings]:          { section: 'Settings',                 icon: 'Settings' },
  [ROUTES.teamManagement]:    { section: 'Team',                     icon: 'Users' },
  [ROUTES.permissions]:       { section: 'Permissions',              icon: 'ShieldCheck' },
  [ROUTES.governanceRecords]: { section: 'Governance Records',       icon: 'BookOpen' },
  [ROUTES.counterparties]:    { section: 'Counterparty Directory',   icon: 'Users' },
  [ROUTES.auditTrail]:        { section: 'Audit Trail',              icon: 'ClipboardList' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCategoryIcon(category) {
  const map = { Assets: 'Building2', Deals: 'Briefcase', Documents: 'FileText', People: 'User', Pages: 'Layout' };
  return map?.[category] || 'Search';
}

function getCategoryColor(category) {
  const map = {
    Assets: 'text-[#3b4c4c] bg-[rgba(59,76,76,0.07)]',
    Deals: 'text-amber-600 bg-amber-50',
    Documents: 'text-purple-600 bg-purple-50',
    People: 'text-green-600 bg-green-50',
    Pages: 'text-slate-600 bg-slate-100',
  };
  return map?.[category] || 'text-slate-600 bg-slate-100';
}

const ENTITY_TYPE_ICONS  = { fund: 'Building2', spv: 'Layers', deal: 'Briefcase' };
const ENTITY_TYPE_LABELS = { fund: 'Fund', spv: 'SPV', deal: 'Deal' };

// ─── Spotlight Search Modal ───────────────────────────────────────────────────
// Spotlight intentionally stays light — it's an overlay panel on any background.

function SpotlightSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setClosing(false);
      setVisible(true);
      setQuery('');
      setTimeout(() => inputRef?.current?.focus(), 60);
    } else if (visible) {
      setClosing(true);
      const t = setTimeout(() => { setVisible(false); setClosing(false); }, 220);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e) => { if (e?.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const allData = [...ASSETS_DATA, ...DEALS_DATA, ...DOCS_DATA, ...PEOPLE_DATA, ...ALL_PAGES];

  const results = query?.trim()?.length > 0
    ? allData?.filter((item) =>
        item?.label?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.subtitle?.toLowerCase()?.includes(query?.toLowerCase())
      )?.slice(0, 20)
    : [];

  const grouped = results?.reduce((acc, item) => {
    if (!acc?.[item?.category]) acc[item?.category] = [];
    acc?.[item?.category]?.push(item);
    return acc;
  }, {});

  const handleSelect = (path) => { navigate(path); onClose(); };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes spotlight-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spotlight-backdrop-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes spotlight-panel-in { from { opacity: 0; transform: translateY(-12px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes spotlight-panel-out { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(-8px) scale(0.97); } }
      `}</style>
      <div
        className="fixed inset-0 flex items-start justify-center pt-24 px-4"
        style={{ zIndex: 200, background: 'rgba(0,0,0,0.55)', animation: `${closing ? 'spotlight-backdrop-out' : 'spotlight-backdrop-in'} 220ms cubic-bezier(0.4,0,0.2,1) both` }}
        onClick={(e) => { if (e?.target === e?.currentTarget) onClose(); }}
      >
        <div
          className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
          style={{ animation: `${closing ? 'spotlight-panel-out' : 'spotlight-panel-in'} 220ms cubic-bezier(0.4,0,0.2,1) both` }}
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
            <Icon name="Search" size={20} className="text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
              placeholder="Search assets, deals, documents, people…"
              className="flex-1 text-base text-slate-900 placeholder-slate-400 outline-none bg-transparent"
            />
            <button onClick={onClose} className="flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {query?.trim()?.length === 0 ? (
              <div className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Quick Links</p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_LINKS?.map((link) => (
                    <button key={link?.path} onClick={() => handleSelect(link?.path)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-[#3b4c4c]/30 hover:bg-slate-50 transition-all text-left group">
                      <span className="flex items-center justify-center w-7 h-7 rounded-md bg-[#3b4c4c]/8 text-[#3b4c4c]">
                        <Icon name={link?.icon} size={15} />
                      </span>
                      <span className="text-sm font-medium text-slate-700 group-hover:text-[#3b4c4c]">{link?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : results?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Icon name="SearchX" size={32} className="mb-2 opacity-40" />
                <p className="text-sm">No results for &ldquo;{query}&rdquo;</p>
              </div>
            ) : (
              <div className="py-2">
                {Object.entries(grouped)?.map(([category, items]) => (
                  <div key={category}>
                    <p className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{category}</p>
                    {items?.map((item, idx) => (
                      <button key={`${item?.path}-${idx}`} onClick={() => handleSelect(item?.path)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left group">
                        <span className={`flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 ${getCategoryColor(item?.category)}`}>
                          <Icon name={getCategoryIcon(item?.category)} size={14} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-slate-800 truncate">{item?.label}</span>
                          {item?.subtitle && <span className="block text-xs text-slate-400 truncate">{item?.subtitle}</span>}
                        </span>
                        <Icon name="ArrowRight" size={14} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 bg-slate-50">
            <span className="text-xs text-slate-400">Press <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-500 font-mono text-xs">Esc</kbd> to close</span>
            <span className="text-xs text-slate-400">Navigate with <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-500 font-mono text-xs">&uarr;&darr;</kbd></span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Context Item ─────────────────────────────────────────────────────────────

function ContextItem({ ctx, isActive, onSelect }) {
  const profile    = ROLE_PROFILES?.[ctx.role];
  const entityIcon = ENTITY_TYPE_ICONS[ctx.entityType]  || 'Building2';
  const entityLabel= ENTITY_TYPE_LABELS[ctx.entityType] || ctx.entityType;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mx-2 mb-0.5 transition-colors ${isActive ? 'cursor-default' : 'cursor-pointer hover:bg-white/10'}`}
      style={{
        background: isActive ? `${profile?.color}20` : undefined,
        border: isActive ? `1px solid ${profile?.color}50` : '1px solid transparent',
      }}
      onClick={() => !isActive && onSelect(ctx.id)}
    >
      <span
        className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
        style={{ background: isActive ? `${profile?.color}25` : 'rgba(255,255,255,0.08)' }}
      >
        <Icon name={entityIcon} size={15} color={isActive ? profile?.color : 'rgba(255,255,255,0.55)'} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight" style={{ color: 'rgba(255,255,255,0.88)' }}>{ctx.entity}</p>
        <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>{entityLabel} · {profile?.label}</p>
      </div>
      {isActive ? (
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ color: profile?.color, background: `${profile?.color}25` }}
        >Active</span>
      ) : (
        <Icon name="ChevronRight" size={14} color="rgba(255,255,255,0.25)" className="flex-shrink-0" />
      )}
    </div>
  );
}

// ─── Context Switcher Panel ───────────────────────────────────────────────────
// Panel renders as a light dropdown over any background — intentional.

const ContextSwitcherPanel = React.forwardRef(function ContextSwitcherPanel({ onClose }, ref) {
  const navigate = useNavigate();
  const { activeContext, availableContexts, setContext, profile } = useRole();

  const handleSelect = (id) => {
    setContext(id);
    onClose();
  };

  const handleLogout = () => { navigate('/'); onClose(); };

  const internalContexts = availableContexts.filter((c) => c.group === 'internal');
  const externalContexts = availableContexts.filter((c) => c.group === 'external');

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="rounded-xl overflow-hidden"
      style={{
        position: 'fixed',
        top: 'calc(var(--header-height) + 6px)',
        right: '1rem',
        zIndex: 200,
        width: '320px',
        background: 'var(--color-nav-panel)',
        border: '1px solid var(--color-nav-panel-border)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.70), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      {/* Identity card — off-white zone */}
      <div
        className="px-4 pt-3.5 pb-0 flex items-center gap-3"
        style={{ background: 'var(--color-nav-context-bg)' }}
      >
        <span
          className="flex items-center justify-center w-9 h-9 rounded-full font-bold flex-shrink-0"
          style={{ background: 'rgba(59,76,76,0.15)', color: '#1e2b2b', border: '1px solid rgba(59,76,76,0.22)', fontSize: '0.7rem' }}
        >
          {DEMO_USER.initials}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight" style={{ color: '#1e2b2b' }}>{DEMO_USER.name}</p>
          <p className="text-xs truncate" style={{ color: '#3b4c4c' }}>{DEMO_USER.email}</p>
        </div>
        <span className="text-xs font-caption" style={{ color: '#6b7b7b' }}>Demo Mode</span>
      </div>

      {/* Acting as strip — grouped with identity card on same off-white surface */}
      <div
        className="px-4 pt-2.5 pb-3"
        style={{ background: 'var(--color-nav-context-bg)', borderBottom: '1px solid rgba(59,76,76,0.15)' }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(59,76,76,0.55)', fontSize: '0.65rem', letterSpacing: '0.05em' }}>Acting as</p>
        <div className="flex items-center gap-2">
          <span
            className="flex items-center justify-center w-5 h-5 rounded-full text-white font-bold flex-shrink-0"
            style={{ background: profile?.color, fontSize: '0.5rem' }}
          >
            {profile?.initials}
          </span>
          <span className="text-sm font-medium" style={{ color: profile?.color }}>{profile?.label}</span>
          <span className="mx-0.5" style={{ color: 'rgba(59,76,76,0.30)' }}>·</span>
          <span className="text-xs truncate" style={{ color: '#3b4c4c' }}>{activeContext?.entity}</span>
        </div>
      </div>

      {/* Context lists — dark navigation zone */}
      <div className="max-h-72 overflow-y-auto py-2">
        <div className="px-4 py-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', letterSpacing: '0.06em' }}>Internal</p>
        </div>
        {internalContexts.map((ctx) => (
          <ContextItem key={ctx.id} ctx={ctx} isActive={ctx.id === activeContext?.id} onSelect={handleSelect} />
        ))}
        <div className="px-4 py-1.5 mt-1">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', letterSpacing: '0.06em' }}>External (Deal-scoped)</p>
        </div>
        {externalContexts.map((ctx) => (
          <ContextItem key={ctx.id} ctx={ctx} isActive={ctx.id === activeContext?.id} onSelect={handleSelect} />
        ))}
      </div>

      {/* Footer actions — heavier terminal zone */}
      <div className="py-1" style={{ background: 'rgba(0,0,0,0.18)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => { navigate(ROUTES.settings); onClose(); }}
          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-white/10"
          style={{ color: 'rgba(255,255,255,0.75)' }}
        >
          <Icon name="Settings" size={15} color="rgba(255,255,255,0.45)" />
          Settings
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-red-500/10"
          style={{ color: '#f87171' }}
        >
          <Icon name="LogOut" size={15} color="#f87171" />
          Log Out
        </button>
      </div>
    </div>,
    document.body
  );
});

// ─── Bottom Strip — Context-aware tabs / breadcrumb ───────────────────────────

const HEADER_BG    = { background: 'var(--color-header)', borderColor: 'var(--color-header-border)' };
const STRIP_BG     = { background: '#1a2525', borderColor: 'var(--color-header-border)' };
const MUTED_FG     = 'rgba(255,255,255,0.58)';
const STRIP_DIVIDER= 'rgba(255,255,255,0.10)';

function BottomStrip({ pathname }) {
  const navigate = useNavigate();
  const tabsRef  = useRef(null);

  const activeSection = SECTION_TABS.find((s) => s.test(pathname));
  const tabs          = activeSection?.tabs  || null;
  const sectionLabel  = activeSection?.label || '';
  const sectionKey    = activeSection?.key   || '';
  const flatPage      = FLAT_PAGE_LABELS[pathname];

  if (!tabs && !flatPage) {
    return <div className="h-12 border-b" style={STRIP_BG} />;
  }

  if (flatPage) {
    return (
      <div className="h-12 border-b flex items-center px-6 gap-2" style={STRIP_BG}>
        <Icon name={flatPage?.icon} size={15} color={MUTED_FG} />
        <span className="text-sm font-medium" style={{ color: MUTED_FG }}>{flatPage?.section}</span>
        <span className="mx-1" style={{ color: 'rgba(255,255,255,0.20)' }}>›</span>
        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.70)' }}>{flatPage?.section}</span>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes tabs-slide-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .tabs-animate { animation: tabs-slide-in 200ms cubic-bezier(0.4, 0, 0.2, 1) both; }
      `}</style>
      <div key={sectionKey} className="tabs-animate h-12 border-b flex items-center" style={STRIP_BG}>
        {/* Section label */}
        <div className="flex items-center gap-2 px-4 flex-shrink-0">
          <span className="text-xs font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color: MUTED_FG }}>
            {sectionLabel}
          </span>
          <div className="w-px h-4" style={{ background: STRIP_DIVIDER }} />
        </div>
        {/* Tabs */}
        <div ref={tabsRef} className="flex items-center h-full overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {tabs?.map((tab) => {
            const isActive = pathname === tab?.path || pathname?.startsWith(tab?.path + '/');
            return (
              <button
                key={tab?.path}
                onClick={() => navigate(tab?.path)}
                className={`flex items-center h-full px-4 text-sm whitespace-nowrap border-b-2 transition-all duration-150 flex-shrink-0 ${
                  isActive
                    ? 'text-white border-white font-medium'
                    : 'border-transparent hover:text-white hover:border-white/30'
                }`}
                style={!isActive ? { color: 'rgba(255,255,255,0.65)' } : {}}
              >
                {tab?.label}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Main GlobalHeader ────────────────────────────────────────────────────────

export default function GlobalHeader({
  sidebarWidth = 280,
  isSidebarCollapsed = false,
  currentRole,
  onRoleChange,
  onMobileMenuOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location?.pathname;

  const { role, profile: roleProfile, activeContext } = useRole();

  const unreadNotifCount = useMemo(() => {
    return NOTIFICATIONS_SEED?.filter((item) => {
      if (item?.roleTarget && !item?.roleTarget?.includes(role)) return false;
      if (item?.contextIds && !item?.contextIds?.includes(activeContext?.id)) return false;
      return !item?.isRead && !item?.isArchived;
    })?.length ?? 0;
  }, [role, activeContext]);

  const unreadMsgCount = useMemo(() => {
    return THREADS?.filter((item) => {
      if (item?.roleTarget && !item?.roleTarget?.includes(role)) return false;
      if (item?.participantContextIds && !item?.participantContextIds?.includes(activeContext?.id)) return false;
      return true;
    })?.reduce((sum, t) => sum + (t?.unreadCount ?? 0), 0) ?? 0;
  }, [role, activeContext]);

  const [isSearchOpen,      setIsSearchOpen]      = useState(false);
  const [isContextPanelOpen,setIsContextPanelOpen] = useState(false);

  const triggerRef      = useRef(null);
  const contextPanelRef = useRef(null);

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handleKey = (e) => {
      if ((e?.metaKey || e?.ctrlKey) && e?.key === 'k') { e?.preventDefault(); setIsSearchOpen(true); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Context panel dismiss — outside click + Escape
  useEffect(() => {
    if (!isContextPanelOpen) return;
    const handleMouseDown = (e) => {
      if (contextPanelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setIsContextPanelOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsContextPanelOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isContextPanelOpen]);

  return (
    <>
      <SpotlightSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Header wrapper — black, flush with sidebar */}
      <header
        className="fixed top-0 right-0 border-b transition-all duration-250"
        style={{
          left: `${sidebarWidth}px`,
          zIndex: 40,
          height: '80px',
          background: 'var(--color-header)',
          borderColor: 'var(--color-header-border)',
        }}
      >
        {/* ── TOP STRIP (32px) — dark green, matches quick access panel ── */}
        <div
          className="h-8 flex items-center justify-between px-4 border-b"
          style={{ background: 'var(--color-nav-panel)', borderColor: 'var(--color-header-border)' }}
        >
          {/* Left side */}
          <div className="flex items-center gap-2">
            {/* Mobile hamburger */}
            <button
              onClick={onMobileMenuOpen}
              className="lg:hidden flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/10 transition-colors mr-1"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              <Icon name="Menu" size={16} color="currentColor" />
            </button>

            {/* Workspace name */}
            <span className="hidden lg:block text-xs font-semibold text-white">SeedDrive</span>

            {/* Separator */}
            <span className="hidden lg:block w-1 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.20)' }} />

            {/* Demo Mode badge */}
            <span className="hidden lg:flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(74,222,128,0.12)', color: '#86efac' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#86efac' }} />
              Demo Mode Active
            </span>

            {/* Separator */}
            <span className="hidden lg:block w-1 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.20)' }} />

            {/* Acting-as role badge */}
            <span
              className="hidden lg:flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border font-caption"
              style={{
                color: roleProfile?.color,
                background: `${roleProfile?.color}25`,
                borderColor: `${roleProfile?.color}50`,
              }}
            >
              <span
                className="flex items-center justify-center w-4 h-4 rounded-full text-white font-bold"
                style={{ background: roleProfile?.color, fontSize: '0.5rem' }}
              >
                {roleProfile?.initials}
              </span>
              Acting as · {roleProfile?.label}
            </span>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Search pill */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="hidden lg:flex items-center gap-2 h-6 px-3 rounded-full text-xs transition-colors hover:bg-white/15"
              style={{ background: 'var(--color-header-search-bg)', color: 'var(--color-header-muted-fg)' }}
            >
              <Icon name="Search" size={12} color="currentColor" />
              <span>Search platform…</span>
            </button>

            {/* ⌘K hint */}
            <span className="hidden xl:flex items-center gap-1 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              <kbd
                className="px-1 py-0.5 rounded font-mono text-xs"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.40)' }}
              >⌘K</kbd>
            </span>

            {/* Notifications */}
            <button
              onClick={() => navigate('/notifications')}
              className="relative flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/10 transition-colors"
              style={{ color: 'rgba(255,255,255,0.65)' }}
              title="Notifications"
            >
              <Icon name="Bell" size={16} color="currentColor" />
              {unreadNotifCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white font-bold"
                  style={{ fontSize: '0.55rem' }}
                >{unreadNotifCount}</span>
              )}
            </button>

            {/* Messages */}
            <button
              onClick={() => navigate('/messages')}
              className="relative flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/10 transition-colors"
              style={{ color: 'rgba(255,255,255,0.65)' }}
              title="Messages"
            >
              <Icon name="MessageSquare" size={16} color="currentColor" />
              {unreadMsgCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full text-white font-bold"
                  style={{ fontSize: '0.55rem', background: '#ef4444' }}
                >{unreadMsgCount}</span>
              )}
            </button>

            {/* User avatar — opens context switcher */}
            <button
              ref={triggerRef}
              onClick={() => setIsContextPanelOpen((prev) => !prev)}
              className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-white/10 transition-colors"
            >
              <span
                className="flex items-center justify-center w-6 h-6 rounded-full text-white font-semibold"
                style={{ background: 'rgba(59,76,76,0.30)', fontSize: '0.6rem', border: '1px solid rgba(59,76,76,0.50)' }}
              >
                {DEMO_USER.initials}
              </span>
              <span
                className="hidden lg:block text-xs font-medium"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                {DEMO_USER.name}
              </span>
              <span
                className="transition-transform duration-200 inline-flex"
                style={{ transform: isContextPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <Icon name="ChevronDown" size={12} color="rgba(255,255,255,0.40)" />
              </span>
            </button>
            {isContextPanelOpen && (
              <ContextSwitcherPanel ref={contextPanelRef} onClose={() => setIsContextPanelOpen(false)} />
            )}
          </div>
        </div>

        {/* ── BOTTOM STRIP (48px) — desktop only ── */}
        <div className="hidden lg:block h-12">
          <BottomStrip pathname={pathname} />
        </div>
      </header>
    </>
  );
}
